import type { SelectedSlot } from "../bookingFlowMachine"
import {
  ScreeningProgram,
  availableDatesQueryKey,
  useAvailabilityForDate,
  useAvailableDates,
  type AvailabilitySlot
} from "../hooks"
import { formatSlotTime, formatTimeZoneLabel, getDisplayTimeZone, getLocalToday, getSlotLocalDate } from "../timezone"
import {
  Alert,
  BackIcon,
  Box,
  Button,
  ForwardIcon,
  IconButton,
  Theme,
  Tile,
  Typography,
  dayjs,
  useMediaQuery
} from "@carrotfertility/carotene-core"
import { FormButton, useFormContext, useWatch } from "@carrotfertility/carotene-core-x"
import { useQueryClient } from "@tanstack/react-query"
import { Skeleton, useNow } from "@internal/ui"
import React from "react"
import { useIntl } from "react-intl"
import { Link } from "react-router-dom"
import { useBookingTracking } from "../tracking"

// 6 days per page (matching the design), which also divides the 30-day horizon evenly.
const DAYS_PER_PAGE = 6

const MOBILE_DAYS_PER_PAGE = 3

// The subset of the preferences form this section reads/writes. `selectedSlot` matches the
// machine's SelectedSlot so it can be handed straight to CONFIRM_BOOKING.
type AvailabilityFormValues = {
  selectedDate: string | null
  selectedSlot: SelectedSlot | null
}

type AppointmentAvailabilityProps = {
  program: ScreeningProgram
  // The submitted visit type + location of care that key the availability query. This
  // component only renders once the member has submitted those (see SelectPreferencesPage),
  // so both are always present. `offeringId` is the chosen offering's scheduling-api id.
  offeringId: string
  stateCode: string
  // State of the booking call the parent fires on submit — drives the CTA's pending/disabled
  // state and the inline error on failure.
  booking: { isPending: boolean; isError: boolean }
  onBack: () => void
  timeZone?: string
}

export function AppointmentAvailability({
  program,
  offeringId,
  stateCode,
  booking,
  onBack,
  timeZone
}: AppointmentAvailabilityProps): JSX.Element {
  const intl = useIntl()
  const tracking = useBookingTracking()
  const queryClient = useQueryClient()
  const displayTimeZone = getDisplayTimeZone(timeZone)
  const isMobile = useMediaQuery((theme: Theme) => theme.breakpoints.down("sm"))
  const { setValue } = useFormContext<AvailabilityFormValues>()
  const selectedDate = useWatch<AvailabilityFormValues, "selectedDate">({ name: "selectedDate" })
  const selectedSlot = useWatch<AvailabilityFormValues, "selectedSlot">({ name: "selectedSlot" })

  const [pageOffset, setPageOffset] = React.useState(0)

  // Local today is only a query-key marker here (the BFF derives the horizon); it changes at local
  // midnight, which can be 23 or 25 hours away across DST, so re-render exactly at the crossing.
  const todayDate = getLocalToday(displayTimeZone).format("YYYY-MM-DD")
  const tomorrowDate = dayjs.utc(todayDate).add(1, "day").format("YYYY-MM-DD")
  useNow(dayjs.tz(tomorrowDate, displayTimeZone).toDate(), "at-the-crossing-only")

  const language = intl.locale
  const searchContext = React.useMemo(
    () => ({ program, offeringId, stateCode, language, timezone: displayTimeZone }),
    [program, offeringId, stateCode, language, displayTimeZone]
  )
  const searchKey = `${program}:${offeringId}:${stateCode}:${language}:${todayDate}:${displayTimeZone}`

  React.useEffect(() => {
    setPageOffset(0)
    setValue("selectedDate", null)
    setValue("selectedSlot", null)
  }, [searchKey, setValue])

  const datesQuery = useAvailableDates(searchContext, { localToday: todayDate })
  const index = datesQuery.data
  const availableDates = React.useMemo(() => {
    if (!index || index.outcome !== "slots_available") return new Set<string>()
    return new Set(index.dates.filter((date) => date >= index.from && date < index.to))
  }, [index])
  const hasDates = availableDates.size > 0

  // A selected-day read can reveal the whole step is unavailable (nobody licensed here / not
  // bookable). Remember that per search so the other dates stop looking bookable; a new search
  // starts clean.
  const [stepUnavailableFor, setStepUnavailableFor] = React.useState<string | null>(null)
  const stepUnavailable = stepUnavailableFor === searchKey

  const dayQuery = useAvailabilityForDate(searchContext, {
    localToday: todayDate,
    date: selectedDate,
    enabled: hasDates && !stepUnavailable
  })
  const dayResult = selectedDate !== null ? dayQuery.data : undefined
  const slotsForSelectedDate = React.useMemo<AvailabilitySlot[]>(() => {
    if (!selectedDate || !dayResult || dayResult.outcome !== "slots_available") return []
    return dayResult.slots.filter((slot) => getSlotLocalDate(slot.startTime, displayTimeZone) === selectedDate)
  }, [dayResult, selectedDate, displayTimeZone])

  // BOOKING_NO_DATES comes only from a completed date index with no dates (carrying its outcome). A
  // network error reports nothing; a populated index clears the store's dedup so a later empty search
  // for a different context is counted. Selected-day results never feed this.
  React.useEffect(() => {
    if (!index) return
    tracking.onEvent({ type: "availability", searchKey, outcome: hasDates ? null : index.outcome })
  }, [tracking, searchKey, index, hasDates])

  // A selected day that turns out to have no times: drop any chosen time, and refresh the index once
  // per (search, date) — the ref guard keeps a refreshed index from re-triggering this — so the strip
  // stops offering a date that just lost its last slot. A refer-out outcome additionally takes the
  // whole step to the unavailable state.
  const dayKey = selectedDate !== null ? `${searchKey}:${selectedDate}` : null
  const indexRefreshedForRef = React.useRef<string | null>(null)
  const dayOutcome = dayResult?.outcome
  const dayHasSlots = slotsForSelectedDate.length > 0
  React.useEffect(() => {
    if (!dayKey || !dayOutcome || dayHasSlots) return
    setValue("selectedSlot", null)
    if (dayOutcome === "no_licensed_provider" || dayOutcome === "not_bookable") {
      setStepUnavailableFor(searchKey)
      setValue("selectedDate", null)
    }
    if (indexRefreshedForRef.current === dayKey) return
    indexRefreshedForRef.current = dayKey
    void queryClient.invalidateQueries({ queryKey: availableDatesQueryKey(searchContext, todayDate) })
  }, [dayKey, dayOutcome, dayHasSlots, searchKey, searchContext, todayDate, queryClient, setValue])

  const daysPerPage = isMobile ? MOBILE_DAYS_PER_PAGE : DAYS_PER_PAGE

  // The visible page of dates, built from the SERVER's `[from, to)`. Represent local calendar dates
  // in UTC only for date arithmetic, so DST cannot change pagination. Drop any day at/after the
  // horizon end so the strip never shows a date outside it.
  const horizonStart = dayjs.utc(index?.from ?? todayDate)
  const horizonEnd = index?.to ?? todayDate
  const pageStart = horizonStart.add(pageOffset * daysPerPage, "day")
  const days = Array.from({ length: daysPerPage }, (_, offset) => pageStart.add(offset, "day")).filter(
    (day) => day.format("YYYY-MM-DD") < horizonEnd
  )
  const canGoNext = pageStart.add(daysPerPage, "day").format("YYYY-MM-DD") < horizonEnd

  const chooseDate = (date: string) => {
    setValue("selectedDate", date)
    // A new day invalidates any previously chosen time — before its slots arrive.
    setValue("selectedSlot", null)
  }

  const chooseSlot = (slot: AvailabilitySlot) => {
    setValue("selectedSlot", {
      slotToken: slot.slotToken,
      startTime: slot.startTime,
      offeringId: slot.offeringId,
      availableHostIds: slot.availableHostIds
    })
  }

  // Paging is local state only — no new request. Clear the selection when it leaves the visible page.
  const goToPage = (offset: number) => {
    setPageOffset(offset)
    const start = horizonStart.add(offset * daysPerPage, "day").format("YYYY-MM-DD")
    const end = horizonStart.add((offset + 1) * daysPerPage, "day").format("YYYY-MM-DD")
    if (selectedDate !== null && (selectedDate < start || selectedDate >= end)) {
      setValue("selectedDate", null)
      setValue("selectedSlot", null)
    }
  }

  if (datesQuery.isLoading) {
    return (
      <Box sx={(theme) => ({ display: "flex", flexDirection: "column", gap: theme.spacing(theme.tokens.spacing.sm) })}>
        <Typography variant="h5" component="h2">
          Select a date
        </Typography>
        <Box
          role="status"
          aria-label="Loading appointment dates"
          aria-busy="true"
          sx={(theme) => ({ display: "flex", gap: theme.spacing(theme.tokens.spacing.xs) })}
        >
          {Array.from({ length: daysPerPage }, (_, index) => (
            <Skeleton
              key={index}
              height={104}
              sx={(theme) => ({ flex: "1 1 0", minInlineSize: 56, borderRadius: theme.tokens.borderRadius.md })}
            />
          ))}
        </Box>
      </Box>
    )
  }
  if (!index) {
    return (
      <Box sx={(theme) => ({ display: "flex", flexDirection: "column", gap: theme.spacing(theme.tokens.spacing.sm) })}>
        <Alert severity="error">Something went wrong loading appointment times. Please try again.</Alert>
        <Box>
          <Button variant="outlined" color="secondary" onClick={() => void datesQuery.refetch()}>
            Retry
          </Button>
        </Box>
      </Box>
    )
  }
  // Nothing to book — show the empty state instead of the date strip, with a disabled "Continue"
  // CTA (matching the design). One message covers every no-dates outcome for now; telling
  // no_licensed_provider apart from no_availability for the member is designed but not built.
  if (!hasDates || stepUnavailable) {
    return (
      <Tile
        color="default"
        sx={(theme) => ({
          display: "flex",
          flexDirection: "column",
          alignItems: "flex-start",
          backgroundColor: theme.palette.background.paper,
          gap: theme.spacing(theme.tokens.spacing.lg)
        })}
      >
        <div>
          <Typography variant="h6" sx={(theme) => ({ marginBlockEnd: theme.spacing(theme.tokens.spacing.xs) })}>
            No appointments available right now
          </Typography>
          <Typography variant="body1">
            There are no open times for an appointment in the next 30 days. Your Carrot Companion can help you find care
            sooner.
          </Typography>
        </div>
        <Button component={Link} to={"/messages"} variant="contained" color="secondary">
          Send a message
        </Button>
      </Tile>
    )
  }

  return (
    <Box sx={(theme) => ({ display: "flex", flexDirection: "column", gap: theme.spacing(theme.tokens.spacing.xl) })}>
      <Box sx={(theme) => ({ display: "flex", flexDirection: "column", gap: theme.spacing(theme.tokens.spacing.sm) })}>
        <Typography variant="h5" component="h2">
          Select a date
        </Typography>
        <Box sx={(theme) => ({ display: "flex", alignItems: "stretch", gap: theme.spacing(theme.tokens.spacing.xs) })}>
          {pageOffset > 0 && (
            <StripChevron label="Previous dates" icon={<BackIcon />} onClick={() => goToPage(pageOffset - 1)} />
          )}
          {days.map((day) => {
            const date = day.format("YYYY-MM-DD")
            return (
              <DateCard
                key={date}
                day={day}
                selected={selectedDate === date}
                onSelect={() => chooseDate(date)}
                disabled={!availableDates.has(date) && selectedDate !== date}
              />
            )
          })}
          {canGoNext && (
            <StripChevron label="Next dates" icon={<ForwardIcon />} onClick={() => goToPage(pageOffset + 1)} />
          )}
        </Box>
      </Box>

      {selectedDate && (
        <Box
          sx={(theme) => ({ display: "flex", flexDirection: "column", gap: theme.spacing(theme.tokens.spacing.sm) })}
        >
          <Typography variant="h5" component="h2">
            Available times
          </Typography>
          {dayQuery.isError ? (
            <Box
              sx={(theme) => ({
                display: "flex",
                flexDirection: "column",
                gap: theme.spacing(theme.tokens.spacing.sm)
              })}
            >
              <Alert severity="error">Something went wrong loading times for this date. Please try again.</Alert>
              <Box>
                <Button variant="outlined" color="secondary" onClick={() => void dayQuery.refetch()}>
                  Retry
                </Button>
              </Box>
            </Box>
          ) : !dayResult ? (
            <Box
              role="status"
              aria-label="Loading appointment times"
              aria-busy="true"
              sx={(theme) => ({
                display: "grid",
                gridTemplateColumns: { xs: "repeat(2, 1fr)", sm: "repeat(4, 1fr)" },
                gap: theme.spacing(theme.tokens.spacing.sm),
                maxInlineSize: 560
              })}
            >
              {Array.from({ length: 8 }, (_, index) => (
                <Skeleton
                  key={index}
                  width="100%"
                  height={48}
                  sx={(theme) => ({ borderRadius: theme.tokens.borderRadius.md })}
                />
              ))}
            </Box>
          ) : slotsForSelectedDate.length === 0 ? (
            <Typography color="text.secondary">
              No appointment times are available for this date. Try another date.
            </Typography>
          ) : (
            <Box
              sx={(theme) => ({
                display: "grid",
                gridTemplateColumns: { xs: "repeat(2, 1fr)", sm: "repeat(4, 1fr)" },
                gap: theme.spacing(theme.tokens.spacing.sm),
                maxInlineSize: 560
              })}
            >
              {slotsForSelectedDate.map((slot) => (
                <TimeSlotButton
                  key={slot.slotToken}
                  label={formatSlotTime(slot.startTime, displayTimeZone)}
                  selected={selectedSlot?.slotToken === slot.slotToken}
                  onSelect={() => chooseSlot(slot)}
                />
              ))}
            </Box>
          )}
          {slotsForSelectedDate.length > 0 && (
            <Typography variant="body1">
              Times are in {formatTimeZoneLabel(slotsForSelectedDate[0].startTime, displayTimeZone)}
            </Typography>
          )}
        </Box>
      )}

      {booking.isError && (
        <Alert severity="error">Something went wrong booking your appointment. Please try again.</Alert>
      )}

      <Box
        sx={(theme) => ({
          display: "flex",
          justifyContent: "space-between",
          gap: theme.spacing(theme.tokens.spacing.sm)
        })}
      >
        <FormButton color="secondary" variant="text" startIcon={<BackIcon />} onClick={onBack}>
          Back
        </FormButton>
        <FormButton
          type="submit"
          color="secondary"
          endIcon={<ForwardIcon />}
          disabled={!selectedSlot || booking.isPending}
        >
          Confirm booking
        </FormButton>
      </Box>
    </Box>
  )
}

type DateCardProps = { day: dayjs.Dayjs; selected: boolean; onSelect: () => void; disabled?: boolean }

// One day in the date strip: a tall card of weekday / day-of-month / month, as a selectable
// button. Cards share the row width equally (flex: 1), matching the design.
function DateCard({ day, selected, onSelect, disabled = false }: DateCardProps): JSX.Element {
  return (
    <Box
      component="button"
      type="button"
      disabled={disabled}
      onClick={onSelect}
      aria-pressed={selected}
      aria-label={day.format("dddd, MMMM D")}
      sx={(theme) => ({
        flex: "1 1 0",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        cursor: disabled ? "not-allowed" : "pointer",
        justifyContent: "space-between",
        gap: theme.spacing(theme.tokens.spacing.xxxs),
        minInlineSize: 56,
        minBlockSize: 104,
        paddingBlock: theme.spacing(theme.tokens.spacing.md),
        paddingInline: theme.spacing(theme.tokens.spacing.xs),
        backgroundColor: disabled ? "unset" : theme.palette.background.paper,
        border: disabled
          ? "none"
          : `${theme.tokens.borderWidth.md} solid ${
              // secondary is a workaround because border.light was too dark.
              selected ? theme.palette.secondary.dark : "transparent"
            }`,
        borderRadius: theme.tokens.borderRadius.md
      })}
    >
      <Typography variant="body2" component="span">
        {day.format("ddd")}
      </Typography>
      <Typography
        variant="h3"
        sx={(theme) => ({
          fontWeight: theme.typography.fontWeightBold,
          color: disabled ? theme.palette.text.disabled : theme.palette.text.primary
        })}
        component="span"
      >
        {day.format("D")}
      </Typography>
      <Typography variant="body2" component="span">
        {day.format("MMM")}
      </Typography>
    </Box>
  )
}

type StripChevronProps = { label: string; icon: JSX.Element; onClick: () => void }

// The date-strip pager control, styled as a card-height bordered box to match the design.
function StripChevron({ label, icon, onClick }: StripChevronProps): JSX.Element {
  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        inlineSize: 48,
        minBlockSize: 104
      }}
    >
      <IconButton
        sx={(theme) => ({
          border: `${theme.tokens.borderWidth.md} solid ${theme.palette.secondary.dark}`,
          borderRadius: theme.tokens.borderRadius.round
        })}
        onClick={onClick}
        aria-label={label}
      >
        {icon}
      </IconButton>
    </Box>
  )
}

type TimeSlotButtonProps = { label: string; selected: boolean; onSelect: () => void }

// One appointment time, as a selectable button.
function TimeSlotButton({ label, selected, onSelect }: TimeSlotButtonProps): JSX.Element {
  return (
    <Box
      component="button"
      type="button"
      onClick={onSelect}
      aria-pressed={selected}
      sx={(theme) => ({
        paddingBlock: theme.spacing(theme.tokens.spacing.sm),
        paddingInline: theme.spacing(theme.tokens.spacing.md),
        cursor: "pointer",
        backgroundColor: theme.palette.background.paper,
        border: `${theme.tokens.borderWidth.md} solid ${
          // secondary is a workaround because border.light was too dark.
          selected ? theme.palette.secondary.dark : "transparent"
        }`,
        borderRadius: theme.tokens.borderRadius.md,
        font: "inherit",
        color: theme.palette.text.primary
      })}
    >
      {label}
    </Box>
  )
}
